import { ev2Ajax } from "./Common/Ajax";
import { ibizaportalexp } from "../Shared/AzureDevopsConfig";
import { Ev2ExperimentStage, Ev2ExperimentStatusResults } from "../Types";

const cachedTerminatedRuns: Record<string, Ev2ExperimentStage[]> = {};
const ignoreRollouts: Record<string, boolean> = {};

// Fetch experiment status from external endpoint
export async function fetchExperimentStatuses(
  rolloutIds: string[],
  signal?: AbortSignal,
  onUpdate?: (value: Ev2ExperimentStatusResults) => void,
): Promise<Ev2ExperimentStatusResults> {
  if (!rolloutIds || rolloutIds.length === 0) {
    return {};
  }

  // Separate rolloutIds into cached, and uncached
  const cachedResults: Ev2ExperimentStatusResults = {};
  const uncachedIds: string[] = [];
  for (const id of rolloutIds) {
    if (ignoreRollouts[id]) continue;
    if (cachedTerminatedRuns[id]) {
      cachedResults[id] = cachedTerminatedRuns[id];
    } else {
      uncachedIds.push(id);
    }
  }

  // If all rolloutIds are cached or ignored, return cached results immediately
  if (uncachedIds.length === 0) {
    return cachedResults;
  }

  // Deliver cached results immediately before fetching
  if (onUpdate && Object.keys(cachedResults).length > 0) {
    onUpdate(cachedResults);
  }

  // Make call to fetch statuses for rolloutIds that are not cached or ignored
  const rolloutIdsStr = uncachedIds.join(",");
  const url = `https://ibizafxmcp.azure-test.net/api/experimentstatus?rolloutIds=${encodeURIComponent(
    rolloutIdsStr,
  )}`;
  const { results: response } = await ev2NoCache<{
    results: Ev2ExperimentStatusResults;
  }>(url, { signal });

  // Process response and update cache and ignore list accordingly
  const allResponses = { ...response, ...cachedTerminatedRuns };
  const sortedResponses: Ev2ExperimentStatusResults = {};
  // Ignore rollouts that returned no results
  uncachedIds.forEach((rolloutId) => {
    if (!allResponses[rolloutId]) {
      ignoreRollouts[rolloutId] = true;
    }
  });
  Object.entries(allResponses).forEach(([rolloutId, rollouts]) => {
    cacheTerminatedRun(rolloutId, rollouts);
    sortedResponses[rolloutId] = rollouts.sort((rolloutA, rolloutB) => {
      return rolloutA.endTime.localeCompare(rolloutB.endTime);
    });
  });

  return sortedResponses;
}

// Terminated run is one that is already complete, this means that we don't need
// an updated status for that run so we're ok caching it's results
const cacheTerminatedRun = (
  rolloutId: string,
  rollouts: Ev2ExperimentStage[],
) => {
  if (
    rollouts.every(
      (rollout) => rollout.status.toLocaleLowerCase() === "succeeded",
    )
  ) {
    cachedTerminatedRuns[rolloutId] = rollouts;
  }
};

export function generateExpStudioLink(rolloutId: string): string {
  return `https://exp.microsoft.com/features/${rolloutId}/progressions?workspaceId=${ibizaportalexp}&_treatmentVariable=enableProgressionTemplates:true|forceUnifiedView:true`;
}

// we don't want to use the cache because a lot of endpoints require
// refreshes to ensure fresh data is being pulled
const ev2NoCache = <T>(url: string, settings?: RequestInit) => {
  return ev2Ajax<T>(url, settings, true, true);
};
